
import { MaintenanceMediaTypes } from "@/enums/MaintenanceMediaTypes";
import { v4 as uuidv4 } from 'uuid';
import { optimizePhotos, uploadImageAsync } from '@/utils/upload-image';
import { fileSizeToMB, validFileSize10MB, validVideoDuration10Sec } from '@/utils/fileSizeToMB';
import * as firebase from 'firebase';
import { waitResizedImage } from '@/utils/wait-resized-image';
import { MaintenanceMedia } from '@/models/maintenance/MaintenanceMedia';
import { getRegionName } from "@/utils/functionsRegion";

export default {
  name: 'MaintenanceUploadMedia',
  components: {
  },
  props: {
    requestId: String,
    facilityId: String,
    isMultiple: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: true,
      mediaLoading: false,
      isNewRequest: false,
      request: { id: null, media: [] }
    };
  },
  computed: {
    currentRequest() {
      return this.$store.state.maintenanceModule.currentMaintenanceRequest;
    },
    MaintenanceMediaTypes() {
      return MaintenanceMediaTypes;
    },
    user() {
      return this.$store.state.authModule.user;
    },
    maintenanceFormsRequestJob() {
      const forms = this.$store.state.maintenanceModule.maintenanceFormsRequestJob;
      const sortOrder = this.request?.formsIds || [];

      const sortMap = Object.fromEntries(sortOrder.map((id, index) => [id, index]));

      return forms.slice().sort((a, b) => {
        return (sortMap[a.id] ?? Infinity) - (sortMap[b.id] ?? Infinity);
      });
    }
  },
  async created() {
    if (this.requestId) {
      await this.$store.dispatch('getMaintenanceRequest', this.requestId);
      this.request = this.currentRequest;
      this.request.media = this.request.media || []
      this.isNewRequest = false
    }
    else {
      this.isNewRequest = true
    }
  },
  methods: {
    async uploadMedia(e) {
      this.$emit('onMaintenanceMediaBeforeUpload')
      this.mediaLoading = true;
      const files = e.target.files;

      if (!files || !files.length) { 
        this.mediaLoading = false; 
        return; 
      }

      if (this.isNewRequest === true) {
        const ref = firebase.firestore().collection('maintenanceRequests').doc();
        this.request.id = ref.id
      }

      if (files.length) {
        try {
          const promises = [];

          // if not multiple, reset media once and limit to 1 file
          if(!this.isMultiple){ 
            this.request.media = [] 
          }

          const maxCount = this.isMultiple ? files.length : Math.min(files.length, 1); // handle single-file mode

          for (let i = 0; i < maxCount; i++) {
            const path = `${this.facilityId}/maintenanceScheduled/staff/${uuidv4()}`;
            const isFileVideo = ["video/mp4", "video/webm"].includes(files[i].type);
            const file = isFileVideo ? files[i] : await optimizePhotos(files[i], 'background-image');
            const fileSizeInMegabytes = fileSizeToMB(file?.size);
            let isValidFileSize = false
            if(isFileVideo){
              isValidFileSize = await validVideoDuration10Sec(file);
            }
            else{
              isValidFileSize = validFileSize10MB(fileSizeInMegabytes);
            }

            if (!isValidFileSize) {
              this.$refs.inputUploadMedia.value = null;
              this.mediaLoading = false;
              return;
            }

            const promise = uploadImageAsync({ path, file })
              .then(async (storage) => {
                const url = await storage.ref.getDownloadURL();
                let thumbnailUrl = null;

                if (!isFileVideo) {
                  const resizedImageRef = await firebase.storage().ref().child(`${path}_200x200`);
                  thumbnailUrl = await waitResizedImage(10, resizedImageRef);
                }

                const thumbnail = isFileVideo ? url : thumbnailUrl;
                const type = isFileVideo ? MaintenanceMediaTypes.Video : MaintenanceMediaTypes.Photo;

                this.request.media.push(new MaintenanceMedia(url, thumbnail, type).toObject());

                return true;
              });

            promises.push(promise);
          }

          await Promise.all(promises).then(async () => {
            await firebase.app().functions(getRegionName())
            .httpsCallable("maintenance-upsertMaintenanceRequestByQRCode")({
                request: {
                  id: this.request.id,
                  facilityId: this.facilityId,
                  media: this.request.media
                }
              })
              .then(async () => {
                console.log('Maintenance request media updated successfully')
              }).catch((err) => {
                console.log(err)
                throw new Error('Unable to upsert maintenance request')
              })

            this.$emit('onMaintenanceMediaUploaded', {id: this.request.id, media: this.request.media })

            this.$toast.open({
              message: files.length > 1 ? this.$t('Gallery.SuccessUploadingImages') : this.$t('Gallery.SuccessUploadingImage'), type: "success"
            });

            this.clearInputFile();
          })
        } catch (error) {
          console.error("Error uploading images:", error);
          this.$toast.open({ type: "error", message: this.$t("GeneralTranslations.UnhandledError")})
        } finally {
          this.mediaLoading = false;
        }
      }
    },

    clearInputFile() {
      const fileInput = document.getElementById('fileInput') as HTMLInputElement;
      fileInput.value = "";
    }
  },
};
